import { CCSPrimaryQuestionIds as Q } from '@chiroup/core/constants/CCSQuestionIds';
import { SectionTitles } from '@chiroup/core/enums/SectionTitles.enum';
import {
  buildFlexibleSections,
  FlexibleDisplaySection,
} from '@chiroup/core/functions/buildFlexibleDisplay';
import {
  ComplaintSummary,
  ComplaintSummaryType,
  QuestionAnswer,
} from '@chiroup/core/types/ComplaintSummary.type';
import { PatientSurvey } from '@chiroup/core/types/PatientSurvey.type';
import { CarePlanComplaint, ComplaintStatuses, Visit } from '@chiroup/core/types/PatientVisit.type';
import { BodyParts, SourceOfPain } from '@chiroup/core/types/Survey.type';
import anitaBackPhoto from './anitaBack.png';

/**
 * Fictitious sample encounter for the Interactive SOAP Sandbox (TTFV Story 5):
 * "Anita Back", the Aligned for Success demo patient, intake CCS from
 * 5/20/26 (fully fictional — confirmed with Dr. B / CJ).
 *
 * TODO(soap-sandbox fixture): the answer content below is representative
 * placeholder data authored to render through buildFlexibleSections. Replace
 * the answer values (NOT the structure) with the real export of Anita Back's
 * 5/20/26 intake — CCS, Yellow Flags, Oswestry, and Review of Systems — once
 * the read-only extraction has been run. Suggested findings/treatments must
 * come from CUP STOCK condition protocols (see fixtures/sandboxConditions).
 *
 * Everything here is static, in-memory data. The sandbox never reads or
 * writes real patient records.
 */

export const SANDBOX_COMPLAINT_ID = 'sandbox-complaint-anita-lumbar';
export const SANDBOX_ENCOUNTER_ID = 'sandbox-encounter-anita';
export const SANDBOX_PATIENT_ID = 'sandbox-patient-anita-back';

/** Survey completion date from the spec: intake CCS from 5/20/26. */
const SURVEY_COMPLETED_AT = new Date('2026-05-20T14:30:00Z').getTime();

export const SANDBOX_PATIENT: {
  ID: string;
  fname: string;
  lname: string;
  gender: string;
  dateOfBirth: string;
  /** Anita's profile illustration (tester note #8); the header falls back to
   *  the initials avatar when undefined. */
  photo?: string;
} = {
  ID: SANDBOX_PATIENT_ID,
  fname: 'Anita',
  lname: 'Back',
  gender: 'female',
  dateOfBirth: '1985-03-14',
  photo: anitaBackPhoto,
};

const radio = (questionText: string, selected: string, others: string[] = []): QuestionAnswer => ({
  questionText,
  questionType: 'radio',
  selectedValues: [selected],
  options: [selected, ...others].map((display, i) => ({
    id: `opt-${i}`,
    display,
    selected: display === selected,
  })),
});

const checkbox = (
  questionText: string,
  selected: string[],
  others: string[] = []
): QuestionAnswer => ({
  questionText,
  questionType: 'checkbox',
  selectedValues: selected,
  options: [...selected, ...others].map((display, i) => ({
    id: `opt-${i}`,
    display,
    selected: selected.includes(display),
  })),
});

const slider = (questionText: string, value: number, max = 10): QuestionAnswer => ({
  questionText,
  questionType: 'slider',
  selectedValues: [String(value)],
  options: [],
  sliderConfig: { min: 0, max, step: 1 },
});

const dateAnswer = (questionText: string, isoDate: string): QuestionAnswer => ({
  questionText,
  questionType: 'date',
  selectedValues: [isoDate],
  options: [],
});

/** Anita's intake CCS answers, keyed by the canonical primary-CCS question ids
 *  so buildFlexibleSections resolves them into the standard Onset /
 *  Provocative / Palliative / Quality / Severity / Timing sections. */
const complaintRelatedQuestions: Record<string, QuestionAnswer> = {
  // Selected labels here drive the Quality section ("<label> described as ...")
  // and gate the VAS sliders — they are symptom-type labels, not locations.
  [Q.mainQuestionId]: checkbox(
    'How would you describe your low back symptoms?',
    ['Pain / Discomfort', 'Stiffness / Tightness'],
    ['Altered sensation', 'Weakness', 'Noise / Grinding']
  ),
  [Q.bestDescribesQuestionPainId]: checkbox(
    'What best describes your low back Pain / Discomfort?',
    ['Consistently dull and occasionally sharp'],
    ['Sore', 'Dull / aching', 'Sharp / stabbing', 'Burning', 'Throbbing']
  ),
  [Q.symptomsBeginQuestionId]: radio('How did your low back symptoms begin?', 'Abruptly', [
    'Gradual over time',
  ]),
  [Q.presentEpisodeQuestionId]: dateAnswer(
    'When did your present episode of low back symptoms begin?',
    '2026-05-12'
  ),
  // Must be exactly 'Single activity' (a real option value): the Onset "Cause
  // description" line only renders for Single/Repetitive activity.
  [Q.causedQuestionId]: radio(
    'What do you think caused your low back symptoms?',
    'Single activity',
    ['Repetitive activity', 'Auto accident', 'Employment-related injury', 'No identifiable cause']
  ),
  [Q.brieflyDescribeQuestionId]: {
    questionText: 'Briefly describe what you think caused your low back pain:',
    questionType: 'input',
    selectedValues: ['Lifting and twisting while moving boxes at home'],
    options: [],
  },
  [Q.onsetQuestionId]: radio(
    'Since the onset, do you feel that your low back symptoms are:',
    'Worsening',
    ['Improving', 'Not changing significantly']
  ),
  [Q.sameSymptomsId]: radio(
    'Have you had the same or very similar low back symptoms in the past?',
    'No',
    ['Yes']
  ),
  [Q.aggravatesQuestionId]: checkbox(
    'What aggravates your symptoms?',
    ['Prolonged sitting', 'Bending forward'],
    ['Lifting', 'Walking', 'Standing']
  ),
  [Q.reliefQuestionId]: checkbox(
    'What relieves your symptoms?',
    ['Walking', 'Changing positions', 'Ice'],
    ['Heat', 'Stretching']
  ),
  [Q.intensityQuestionId]: slider('Average pain severity (VAS)', 6),
  [Q.intensity2QuestionId]: slider('Pain severity at its worst (VAS)', 7),
  [Q.interferedQuestionId]: slider('How much has pain interfered with your enjoyment of life?', 3),
  [Q.generalInterferedQuestionId]: slider(
    'How much has pain interfered with your general activity?',
    4
  ),
  [Q.symptomsArePresentQuestionId]: slider('Symptoms are present (% of days)', 50, 100),
  [Q.percentOfDaysQuestionId]: slider('When present, symptoms last (% of the day)', 45, 100),
  // Deliberately unanswered: the seek-care answer rendered an "Additional
  // information / Sought care: No" block in the CCS review and the generated
  // note, which the 2026-09-28 requirement removed from the SOAP output.
  [Q.yellowFlagsQuestionId]: checkbox(
    'Do any of the following apply to you?',
    ['I worry my pain means something is seriously wrong'],
    ['I avoid activity because of my pain', 'None of these']
  ),
};

export const SANDBOX_COMPLAINT_SUMMARY: ComplaintSummary = {
  id: 'sandbox-summary-anita',
  complaintId: SANDBOX_COMPLAINT_ID,
  complaintRelatedQuestions,
  createdAt: SURVEY_COMPLETED_AT,
  updatedAt: SURVEY_COMPLETED_AT,
  type: ComplaintSummaryType.Initial,
  clinicId: 0,
  patientId: SANDBOX_PATIENT_ID,
  encounterId: SANDBOX_ENCOUNTER_ID,
};

/** Rear lower back (bilateral, right-dominant) + right hip, per the spec's
 *  body-diagram screenshot. */
export const SANDBOX_BODY_PARTS: BodyParts = {
  front: {},
  rear: {
    'lower-back': { left: true, right: true },
    hip: { left: false, right: true },
  },
} as BodyParts;

export const SANDBOX_COMPLAINT: CarePlanComplaint = {
  id: SANDBOX_COMPLAINT_ID,
  complaint: 'Lower back and right hip pain',
  region: 'Lumbosacral',
  status: ComplaintStatuses.ACTIVE,
  added: SURVEY_COMPLETED_AT,
  createdAt: SURVEY_COMPLETED_AT,
  prn: false,
  bodypicker: {
    parts: SANDBOX_BODY_PARTS,
    sourceOfPain: { side: 'rear', x: null, y: null } satisfies SourceOfPain,
  },
  assessment: { conditions: [] },
};

/** Sandbox-only id for the symptom-location line injected below — locations
 *  come from the body picker in the real CCS, not a question, so no canonical
 *  id exists for this. */
export const SANDBOX_LOCATION_QUESTION_ID = 'sandbox-symptom-location';

const SANDBOX_LOCATION_ANSWER: QuestionAnswer = {
  questionText: 'Where are your symptoms located?',
  questionType: 'input',
  selectedValues: ['Lower back and right hip pain'],
  options: [],
};

/** The map InteractiveComplaintSummary resolves template values from. It must
 *  include the injected location answer: the renderer displays "None" for any
 *  template id missing from THIS map (regardless of the item's own
 *  questionData) — the round-2 doc's "Symptoms: None" screenshot. The answer
 *  can't live in the summary itself: unconsumed summary ids fall into the
 *  "Survey Responses" leftovers bucket (tester note #6). */
export const SANDBOX_QUESTION_ANSWER_MAP: Record<string, QuestionAnswer> = {
  ...complaintRelatedQuestions,
  [SANDBOX_LOCATION_QUESTION_ID]: SANDBOX_LOCATION_ANSWER,
};

/** Step 1's hero sections: the real CCS display pipeline over Anita's answers,
 *  plus a "Symptoms: <location>" line atop Onset (tester note #6, 2026-09-08 —
 *  the real builder has no location line, and adding one there would change
 *  every clinic's CCS display, so the sandbox injects it after the fact). */
export const buildAnitaCcsSections = (): FlexibleDisplaySection[] => {
  const sections = buildFlexibleSections(SANDBOX_COMPLAINT_SUMMARY);

  const onset = sections.find(s => s.title === SectionTitles.Onset);
  onset?.items.unshift({
    type: 'text',
    template: `Symptoms: {${SANDBOX_LOCATION_QUESTION_ID}}`,
    questionData: { [SANDBOX_LOCATION_QUESTION_ID]: SANDBOX_LOCATION_ANSWER },
  });

  return sections;
};

/** Intake documents completed before the visit (spec: include CCS, Yellow
 *  Flags, Oswestry, and Review of Systems; the record also carries Medical
 *  History). The CCS renders as the Step 1 hero; these summarize the rest.
 *
 *  TODO(soap-sandbox fixture): replace summaries with Anita Back's real
 *  5/20/26 intake export. */
export const SANDBOX_INTAKE_SURVEYS: {
  id: string;
  name: string;
  /** Bold inline lead of the first paragraph (tester note #9). */
  boldLead?: string;
  paragraphs: string[];
  /** Bold-led line closing the pop-up. Used to echo the positive findings the
   *  generated note reports for this survey, so the pop-up and the note can't
   *  tell different stories (round-3 tester note #3). */
  footer?: { lead: string; text: string };
}[] = [
  {
    id: 'oswestry',
    name: 'Oswestry Disability Index',
    paragraphs: [
      'Score: 24% · Moderate disability',
      'ChiroUp automatically sends the appropriate disability questionnaire based on the patient\u2019s complaint, scores it, adds the results to your note, and tracks progress throughout the care plan.',
    ],
  },
  {
    id: 'review-of-systems',
    name: 'Review of Systems',
    paragraphs: [
      'A Review of Systems can be included with the Chief Complaint Survey, so important findings aren\u2019t missed. ChiroUp highlights positive findings for follow-up.',
    ],
    // Mirrors the positives SANDBOX_COMPLETED_SURVEYS reports for ROS; the
    // old "No red flags reported" lead contradicted them (round-3 note #3).
    footer: { lead: 'Patient reports:', text: 'decreased energy and constipation' },
  },
  {
    id: 'medical-history',
    name: 'Medical History',
    boldLead: 'Patient reports:',
    paragraphs: [
      'Asthma, controlled with an inhaler.',
      'Clinics can choose to include additional surveys, such as medical or surgical history, to collect pertinent information before the visit and automatically incorporate it into the note.',
    ],
  },
];

/**
 * Completed intake surveys appended to the generated Subjective and the final
 * note, exactly as the real wizard appends them (tester note #24): rendered
 * through buildCompletedSurvey (completedSurveysForNotes) so the output format
 * matches the working EHR (bold survey name + score header, Patient reports /
 * Patient denies lines). The sandbox strips the builder's answer colors
 * afterwards (round-2 tester note #9: single-color preview and output).
 *
 * Content is Dr. B's verbatim replacement text (round-2 note #9), shaped so
 * the REAL builder emits those exact lines: radios render
 * "Question: <answer>", checkboxes render the question as a group header then
 * Patient reports / Patient denies (an empty question renders no header).
 */
const surveyRadio = (id: string, question: string, chosen: string, others: string[]) => ({
  id,
  type: 'radio',
  question,
  options: [chosen, ...others].map((display, i) => ({ id: `${id}-o${i}`, display })),
  answer: { type: 'radio', value: `${id}-o0` },
});

const surveyCheckbox = (id: string, question: string, chosen: string[], others: string[]) => ({
  id,
  type: 'checkbox',
  question,
  options: [...chosen, ...others].map((display, i) => ({ id: `${id}-o${i}`, display })),
  answer: { type: 'checkbox', value: chosen.map((_, i) => `${id}-o${i}`) },
});

const completedSurvey = (
  id: string,
  name: string,
  questions: unknown[],
  score?: string
): PatientSurvey =>
  ({
    id,
    surveyId: id,
    patientId: SANDBOX_PATIENT_ID,
    clinicId: 0,
    order: 0,
    createdAt: SURVEY_COMPLETED_AT,
    updatedAt: SURVEY_COMPLETED_AT,
    completedAt: SURVEY_COMPLETED_AT,
    score,
    survey: { name, questions },
  }) as unknown as PatientSurvey;

export const SANDBOX_COMPLETED_SURVEYS: PatientSurvey[] = [
  completedSurvey(
    'sandbox-oswestry',
    'Oswestry Disability Index',
    [
      surveyRadio('osw-pain', 'Pain Intensity:', 'Pain is mild and does not vary much.', []),
      surveyRadio(
        'osw-care',
        'Personal Care:',
        'Washing and dressing increase pain, but the patient manages without changing how they do it.',
        []
      ),
      surveyRadio(
        'osw-lifting',
        'Lifting:',
        'Can lift heavy weights, but it causes extra pain.',
        []
      ),
      surveyRadio(
        'osw-walking',
        'Walking:',
        'Some pain with walking, but it does not increase with distance.',
        []
      ),
      surveyRadio('osw-sitting', 'Sitting:', 'Pain prevents sitting for more than one hour.', []),
      surveyRadio(
        'osw-standing',
        'Standing:',
        'Some pain with standing, but it does not increase with time.',
        []
      ),
      surveyRadio(
        'osw-sleeping',
        'Sleeping:',
        'Pain in bed, normal night’s sleep is reduced by less than 1/4.',
        []
      ),
      surveyRadio(
        'osw-social',
        'Social Life:',
        'Social life is normal, but it increases the degree of pain.',
        []
      ),
      surveyRadio(
        'osw-traveling',
        'Traveling:',
        'Some pain while traveling, but usual forms of travel do not make it worse.',
        []
      ),
      surveyRadio('osw-changing', 'Changing Degree of Pain:', 'Pain is gradually worsening.', []),
    ],
    '24% (Moderate disability)'
  ),
  completedSurvey('sandbox-ros', 'Review of Systems', [
    surveyCheckbox(
      'ros-general',
      'General',
      ['Decreased energy'],
      [
        'Recent unexplained weight loss',
        'Loss of appetite',
        'Night sweats',
        'Fever or chills',
        'Recurrent infections',
        'Skin ulcers or rashes',
        'Excessive thirst',
        'Sustained morning stiffness (>30 minutes)',
      ]
    ),
    surveyCheckbox(
      'ros-nervous',
      'Nervous system',
      [],
      [
        'Change in vision, smell, hearing, or taste',
        'Lightheadedness or dizziness',
        'Loss of consciousness',
        'Difficulty speaking or swallowing',
        'Headaches',
        'Numbness or tingling',
        'Difficulty walking',
        'Change in mood or behavior',
      ]
    ),
    surveyCheckbox(
      'ros-digestive',
      'Digestive',
      ['Constipation'],
      [
        'Nausea',
        'Stomach pain',
        'Difficulty swallowing',
        'Indigestion',
        'Vomiting',
        'Diarrhea',
        'Bloating',
        'Excessive gas or belching',
        'Blood in stool',
        'Black stools',
      ]
    ),
    surveyCheckbox(
      'ros-urinary',
      'Urinary & reproductive',
      [],
      [
        'Difficulty with urination',
        'Burning with urination',
        'Blood in urine',
        'Increased frequency of urination',
        'Loss of bladder or bowel control',
        'Change in menstrual bleeding',
      ]
    ),
  ]),
  completedSurvey('sandbox-medical-history', 'Medical History', [
    // Empty question: Medical History has no sub-header, just the
    // reports/denies lines directly under the survey title.
    surveyCheckbox(
      'mh-conditions',
      '',
      ['Asthma - controlled with an inhaler'],
      [
        'Diabetes',
        'Cancer',
        'Thyroid disease',
        'AIDS/HIV',
        'Mental disorder',
        'Dementia',
        'Fracture',
        'Paralysis',
        'Seizures',
        'Dislocation',
        'Arthritis',
        'Rheumatoid arthritis',
        'Gout',
        'Lupus',
        'Osteoporosis',
        'Scoliosis',
        'Pacemaker',
        'Stroke',
        'High blood pressure',
        'High cholesterol',
        'Heart disease',
        'Defibrillator',
        'Irregular heartbeat',
        'Heart attack',
        'Congestive heart failure',
        'TIA',
        'Peripheral vascular disease',
        'Blood clotting or bleeding disorder',
        'Anemia',
        'Aneurysm',
        'COPD',
        'Emphysema',
        'Tuberculosis',
        'Appendicitis',
        'Gall stones',
        'Liver disease',
        'Hepatitis',
        'Ulcers',
        'Pancreatitis',
        'Kidney disease',
        'Kidney stones',
      ]
    ),
  ]),
];

/** Fresh sandbox encounter seed. A factory (not a shared constant) so every
 *  sandbox run starts clean and mutation never leaks between visits.
 *
 *  planWizardCategory stays undefined on purpose: UnifiedObjectiveStep's
 *  Joints → CMT plan sync treats "no plan wizard config" as
 *  everything-visible, which is exactly the sandbox's controlled default.
 *  The plan seeds the spec's Default Plan State: New Patient E&M preselected
 *  plus one unit of manual therapy; the CMT service arrives via the Joints
 *  sync once spinal segments are selected. */
export const buildSandboxVisit = (): Visit => ({
  id: SANDBOX_ENCOUNTER_ID,
  patientId: SANDBOX_PATIENT_ID,
  visitDate: SURVEY_COMPLETED_AT,
  version: 2,
  signed: null,
  // visitNumber/goal/visitTimes drive the Assessment's "A care plan has been
  // established to include 3 visits per week for 2 weeks" and "Outcome goal:
  // Expect a 33% subjective improvement..." lines (tester note #25) — the
  // note output format is the working EHR's, produced by the production
  // assessment builder from these values.
  encounterInfo: {
    complaints: [structuredClone(SANDBOX_COMPLAINT)],
    visitNumber: 1,
    goal: 33,
    visitTimes: [{ times: 3, timesUnit: 'week', duration: 2, durationUnit: 'week' }],
  },
  complaintSummaries: {
    [SANDBOX_COMPLAINT_ID]: structuredClone(SANDBOX_COMPLAINT_SUMMARY),
  },
  unifiedObjective: {},
  plan: {
    evaluationAndManagement: { '-1': { units: 1, services: ['99202'] } },
    treatments: { manualTherapy: { manualTherapy: { units: 1, services: ['97140'] } } },
  },
});
